import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileCheck, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';

export default function TrainingEvidencePage() {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['training-evidence'],
    queryFn: () => api.training.listEvidence(200),
    staleTime: 60_000,
  });

  const records = (data?.data ?? []) as Record<string, unknown>[];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <FileCheck className="h-7 w-7 text-teal-400" />
        <div>
          <h1 className="text-2xl font-bold">Training Evidence</h1>
          <p className="text-sm text-muted-foreground">Immutable audit-ready records of training acknowledgments</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />)}</div>
      ) : records.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <FileCheck className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium">No training evidence yet</p>
          <p className="text-xs text-muted-foreground">Evidence records are created when staff acknowledge training</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 text-left w-8"></th>
                <th className="px-4 py-2 text-left">Staff</th>
                <th className="px-4 py-2 text-left">Training</th>
                <th className="px-4 py-2 text-left">Regulatory Source</th>
                <th className="px-4 py-2 text-left">Acknowledged</th>
                <th className="px-4 py-2 text-right">Export</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => {
                const name = `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || (r.email as string) || 'Unknown';
                const isExpanded = expanded === (r.id as string);
                return (
                  <>
                    <tr key={r.id as string} className="border-t border-border hover:bg-muted/20 cursor-pointer"
                      onClick={() => setExpanded(isExpanded ? null : (r.id as string))}>
                      <td className="px-4 py-2">
                        {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      </td>
                      <td className="px-4 py-2 font-medium">{name}</td>
                      <td className="px-4 py-2">{r.package_title as string}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {(r.regulatory_reference as string) ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {new Date(r.acknowledged_at as string).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <a
                          href={`/api/training/evidence/${r.id}/pdf`}
                          target="_blank"
                          rel="noopener"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-xs text-teal-400 hover:underline"
                          aria-label="Export as PDF"
                        >
                          <Download className="h-3 w-3" /> PDF
                        </a>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${r.id}-exp`} className="border-t border-border bg-muted/10">
                        <td></td>
                        <td colSpan={5} className="px-4 py-4">
                          <div className="space-y-3 text-xs">
                            <div>
                              <span className="font-semibold uppercase tracking-wide text-muted-foreground">Statement: </span>
                              <span className="italic">{r.acknowledgment_statement as string}</span>
                            </div>
                            <div>
                              <span className="font-semibold uppercase tracking-wide text-muted-foreground">What Changed: </span>
                              <span>{r.what_changed as string}</span>
                            </div>
                            <div className="flex gap-4 pt-2 border-t border-border/50">
                              <span className="text-muted-foreground">
                                IP: <span className="font-mono">{(r.ip_address as string) ?? '—'}</span>
                              </span>
                              <span className="text-muted-foreground">
                                Roles: {((r.roles_affected as string[]) ?? []).join(', ')}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
